import { Link } from 'react-router-dom';
import { FileText, CheckCircle, AlertCircle, Calculator } from 'lucide-react';


interface InputFact {
  factId: string | null;
  canonicalModel: string;
  canonicalId: string;
  field: string;
  value: unknown;
  verified: boolean;
  sourceDocumentId: string | null;
  pageReference: string | null;
}

interface Calculation {
  formula?: string;
  [key: string]: unknown;
}

interface EvidenceContext {
  ruleTriggered?: string;
  inputFacts?: InputFact[];
  calculation?: Calculation;
}

interface Evidence {
  id: string;
  sourceType: string;
  title: string;
  excerpt: string;
  fullContext: EvidenceContext;
  confidence: number;
}

interface Props {
  evidence: Evidence[];
}

export function EvidenceDetail({ evidence }: Props) {
  if (!evidence || evidence.length === 0) {
    return (
      <p className="text-sm text-gray-400 italic">No evidence detail available</p>
    );
  }

  return (
    <div className="space-y-4">
      {evidence.map((ev) => (
        <div key={ev.id} className="bg-gray-50 rounded-lg p-4 text-sm">
          <p className="text-gray-700 mb-3">{ev.excerpt}</p>

          {/* Input facts */}
          {ev.fullContext?.inputFacts && ev.fullContext.inputFacts.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Based on</p>
              <ul className="space-y-1">
                {ev.fullContext.inputFacts.map((fact, i) => (
                  <li key={i} className="flex items-center gap-2 text-gray-600">
                    {fact.verified ? (
                      <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="h-3.5 w-3.5 text-yellow-500 flex-shrink-0" />
                    )}
                    <span>
                      {fact.field}: {formatValue(fact.value)}
                    </span>
                    {fact.sourceDocumentId && (
                      <Link
                        to={`/documents/${fact.sourceDocumentId}`}
                        className="inline-flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FileText className="h-3 w-3" />
                        {fact.pageReference ?? 'View document'}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Calculation */}
          {ev.fullContext?.calculation && (
            <div className="border-t border-gray-200 pt-2 mt-2">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-1 flex items-center gap-1">
                <Calculator className="h-3 w-3" /> Calculation
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                {Object.entries(ev.fullContext.calculation)
                  .filter(([k]) => k !== 'formula')
                  .map(([key, val]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-gray-500">{humanize(key)}:</span>
                      <span className="font-medium">{formatValue(val)}</span>
                    </div>
                  ))}
              </div>
              {ev.fullContext.calculation.formula && (
                <p className="text-xs text-gray-400 mt-1 italic">
                  Formula: {ev.fullContext.calculation.formula}
                </p>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function formatValue(val: unknown): string {
  if (typeof val === 'number') {
    if (val > 100) return `$${val.toLocaleString()}`;
    if (val < 1 && val > 0) return `${(val * 100).toFixed(1)}%`;
    return val.toLocaleString();
  }
  if (val == null) return '—';
  return String(val);
}

function humanize(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
}
